// Admin SPA route tree.
//
// Admins operate platform-wide so this tree intentionally does NOT mount
// `AdminTenantGuard` (no per-tenant context). All routes are gated by
// `AuthGuard` + `ModuleRoleGuard`, the latter resolving the active module
// slug `admin` to the Keycloak realm role `administrator`.

import { Navigate } from 'react-router';

import { AppLayout } from 'app/layouts/AppLayout';
import { DynamicLayout } from 'app/layouts/DynamicLayout';
import AuthGuard from 'middleware/AuthGuard';
import ModuleRoleGuard from 'middleware/ModuleRoleGuard';

import { deviceManagementRoutes, settingsRoutes } from '../sharedRoutes';

// ----------------------------------------------------------------------

const dashboardRoute = {
  path: 'dashboard',
  lazy: async () => ({
    Component: (await import('app/pages/admin/dashboard/AdminDashboardPage'))
      .default,
  }),
};

const stationsRoute = {
  path: 'stations',
  element: <Navigate to="/device-management/stations" replace />,
};

const tenantsRoutes = {
  path: 'tenants',
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import('app/pages/admin/tenants/TenantListPage'))
          .default,
      }),
    },
    {
      path: 'new',
      lazy: async () => ({
        Component: (await import('app/pages/admin/tenants/TenantCreatePage'))
          .default,
      }),
    },
    {
      path: ':tenantId',
      lazy: async () => ({
        Component: (await import('app/pages/admin/tenants/TenantDetailPage'))
          .default,
      }),
    },
    {
      path: ':tenantId/edit',
      lazy: async () => ({
        Component: (await import('app/pages/admin/tenants/TenantEditPage'))
          .default,
      }),
    },
  ],
};

const usersRoutes = {
  path: 'users',
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import('app/pages/admin/users/UserListPage')).default,
      }),
    },
    {
      path: 'new',
      lazy: async () => ({
        Component: (await import('app/pages/admin/users/UserCreatePage'))
          .default,
      }),
    },
    {
      path: ':userId',
      lazy: async () => ({
        Component: (await import('app/pages/admin/users/UserDetailPage'))
          .default,
      }),
    },
    {
      path: ':userId/edit',
      lazy: async () => ({
        Component: (await import('app/pages/admin/users/UserEditPage')).default,
      }),
    },
  ],
};

const countriesRoutes = {
  path: 'countries',
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (
          await import('app/pages/admin/geo/countries/CountryListPage')
        ).default,
      }),
    },
    {
      path: 'new',
      lazy: async () => ({
        Component: (
          await import('app/pages/admin/geo/countries/CountryCreatePage')
        ).default,
      }),
    },
    {
      path: ':countryId/edit',
      lazy: async () => ({
        Component: (
          await import('app/pages/admin/geo/countries/CountryEditPage')
        ).default,
      }),
    },
  ],
};

const statesRoutes = {
  path: 'states',
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/states/StateListPage'))
          .default,
      }),
    },
    {
      path: 'new',
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/states/StateCreatePage'))
          .default,
      }),
    },
    {
      path: ':stateId/edit',
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/states/StateEditPage'))
          .default,
      }),
    },
  ],
};

const citiesRoutes = {
  path: 'cities',
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/cities/CityListPage'))
          .default,
      }),
    },
    {
      path: 'new',
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/cities/CityCreatePage'))
          .default,
      }),
    },
    {
      path: ':cityId/edit',
      lazy: async () => ({
        Component: (await import('app/pages/admin/geo/cities/CityEditPage'))
          .default,
      }),
    },
  ],
};

const geographyRoutes = {
  path: 'geography',
  children: [
    { index: true, element: <Navigate to="countries" replace /> },
    countriesRoutes,
    statesRoutes,
    citiesRoutes,
  ],
};

const auditLogRoute = {
  path: 'audit-log',
  lazy: async () => ({
    Component: (await import('app/pages/admin/auditLog/AuditLogPage')).default,
  }),
};

const helpCenterRoute = {
  path: 'help-center',
  lazy: async () => ({
    Component: (await import('app/pages/admin/helpCenter/AdminHelpCenterPage'))
      .default,
  }),
};

const protectedRoutes = {
  id: 'protected',
  Component: AuthGuard,
  children: [
    {
      Component: ModuleRoleGuard,
      children: [
        {
          Component: DynamicLayout,
          children: [
            { index: true, element: <Navigate to="dashboard" replace /> },
            dashboardRoute,
            tenantsRoutes,
            usersRoutes,
            stationsRoute,
            deviceManagementRoutes,
            geographyRoutes,
            auditLogRoute,
            helpCenterRoute,
          ],
        },
        { Component: AppLayout, children: [settingsRoutes] },
      ],
    },
  ],
};

export { protectedRoutes };
